import type { JSX } from 'react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Apple, Download, MonitorDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api } from '@/lib/api-client';

// Mirrors the backend `app_builds` row (modules/servers/echo-control-plane/src/db/schema.ts, `appBuilds`).
interface AppBuild {
  platform: 'macos' | 'windows' | 'windows-msi';
  filename: string;
  blobPath: string;
  sizeBytes: number;
  sha256: string;
  productVersion: string | null;
  sourceCommit: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
}

const PLATFORM_META: Record<AppBuild['platform'], { label: string; icon: LucideIcon }> = {
  macos: { label: 'macOS (Apple Silicon)', icon: Apple },
  windows: { label: 'Windows (x64)', icon: MonitorDown },
  // The operator-rollout channel (EW-136): per-machine MSI for SCCM/GPO deployment, no
  // auto-update — published beside the per-user NSIS installer above.
  'windows-msi': { label: 'Windows (x64) — MSI, üzemeltetői terítés', icon: MonitorDown },
};

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Downloads(): JSX.Element {
  const builds = useQuery({
    queryKey: ['/api/admin/builds'],
    queryFn: () => api.get<AppBuild[]>('/api/admin/builds'),
  });

  const byPlatform = new Map((builds.data ?? []).map((b) => [b.platform, b]));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Letöltések</h2>
        <p className="text-sm text-slate-500">
          A legfrissebb EchoWork desktop build. A `main`-re pusholáskor a CI automatikusan frissíti.
        </p>
      </div>

      {builds.isLoading && <p className="text-sm text-slate-500">Betöltés…</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        {(['macos', 'windows', 'windows-msi'] as const).map((platform) => (
          <BuildCard key={platform} platform={platform} build={byPlatform.get(platform) ?? null} />
        ))}
      </div>
    </div>
  );
}

function BuildCard({
  platform,
  build,
}: {
  platform: AppBuild['platform'];
  build: AppBuild | null;
}): JSX.Element {
  const { label, icon: Icon } = PLATFORM_META[platform];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    if (!build) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.get<{ url: string }>(`/api/admin/builds/${platform}/sas`);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-slate-700" />
        <span className="font-medium text-slate-900">{label}</span>
      </div>

      {build ? (
        <>
          <dl className="space-y-1 text-xs text-slate-600">
            <div className="flex justify-between">
              <dt>Verzió</dt>
              <dd className="font-mono">{build.productVersion ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Forráskommit</dt>
              <dd className="truncate font-mono" title={build.sourceCommit ?? undefined}>
                {build.sourceCommit ? build.sourceCommit.slice(0, 10) : '—'}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt>Méret</dt>
              <dd>{formatBytes(build.sizeBytes)}</dd>
            </div>
            <div className="flex justify-between">
              <dt>Feltöltve</dt>
              <dd>{new Date(build.uploadedAt).toLocaleString('hu-HU')}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>SHA-256</dt>
              <dd className="truncate font-mono" title={build.sha256}>
                {build.sha256.slice(0, 16)}…
              </dd>
            </div>
          </dl>
          <button className="btn-primary" onClick={() => void download()} disabled={busy}>
            <Download className="h-4 w-4" /> {busy ? 'Link kérése…' : 'Letöltés'}
          </button>
          {error && <p className="text-xs text-rose-600">{error}</p>}
        </>
      ) : (
        <p className="text-sm text-slate-500">Még nincs feltöltött build ehhez a platformhoz.</p>
      )}
    </div>
  );
}
